import {FC} from "react";
import styled from "styled-components";
import {ICard} from "modules/reducers";
import {useTranslation} from "react-i18next";
import {getValueByLang} from "modules/utils";
import {IMAGES_URL} from "modules/constants";
import {CardType, Language} from "modules/enums";
import {PrimaryDarkExternalLink} from "components";
import {ReactComponent as BackgroundImage} from "assets/images/cardHeader.svg";

const Wrapper = styled.div`
	display: flex;
	position: relative;

	@media (min-width: 769px) {
		margin: 0;
		width: 100%;
		flex: 1 0 21%;
		max-width: 320px;

		&:nth-child(4n) {
			margin-right: 0;
		}

		&:first-child:nth-last-child(6),
		&:first-child:nth-last-child(6) ~ div,
		&:first-child:nth-last-child(5),
		&:first-child:nth-last-child(5) ~ div {
			flex: 1 0 26%;

			&:nth-child(3n) {
				margin-right: 0;
			}
		}
	}
`;

const Container = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	flex: 1;
	background: ${({theme}) => theme.background.card};
	color: ${({theme}) => theme.color.card};
	border: 2px solid ${({theme}) => theme.background.active};
	border-radius: 4px;
	overflow: hidden;
`;

const Head = styled.div`
	width: 100%;
	display: flex;
	justify-content: center;
	position: relative;
	z-index: 1;
	height: 170px;
`;

const Background = styled.div<{$background: string}>`
	position: absolute;
	top: 0;
	z-index: -1;
	left: 50%;
	transform: translateX(-50%);

	svg {
		position: absolute;
		left: 50%;
		transform: translateX(-50%);
		height: 175px;
		width: auto;

		path {
			fill: ${({$background}) => $background || "#CCCCCC"};
		}
	}
`;

const Img = styled.img`
	max-width: 90%;
	height: calc(100% - 10px);
	margin-top: 8px;
	object-fit: contain;
`;

const Content = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 16px 20px 20px;
	flex: 1;
`;

const Desc = styled.div`
	flex: 1;
	font-family: "Hind", sans-serif;
	font-size: 18px;
	font-weight: 500;
	line-height: 26px;
	letter-spacing: 0;
	text-align: center;
	margin-bottom: 20px;
`;

const Link = styled(PrimaryDarkExternalLink)`
	display: flex;
	align-items: center;
	justify-content: center;
	text-transform: uppercase;
	position: relative;
	max-width: 188px;
`;

const getClassName = (status: CardType) => {
	let className = "";

	if (status === CardType.COMING_SOON) {
		className += " disabled";
	}

	return className;
};

const getDescClassName = (lang: string) => {
	let className = "";

	if (lang === Language.French) {
		className += " fr";
	}

	return className;
};

interface IProps {
	card: ICard;
}

export const Card: FC<IProps> = ({card}) => {
	const {btnMsg, gameLogoEn, gameLogoFr, gameDescription, gameLink, status} = card;
	const {i18n} = useTranslation();
	const lang = i18n.language;
	const logo = lang === Language.English ? gameLogoEn : gameLogoFr;
	const logoImage = `${IMAGES_URL}image/${logo || ""}`;
	const className = getClassName(status);
	const descClassName = getDescClassName(lang);

	return (
		<Wrapper>
			<Container>
				<Head>
					<Background $background={card.background}>
						<BackgroundImage />
					</Background>

					<Img src={logoImage} role="presentation" />
				</Head>

				<Content>
					<Desc className={descClassName}>{getValueByLang(gameDescription, lang)}</Desc>

					<Link
						href={getValueByLang(gameLink, lang)}
						className={className}
						target="_self">
						{getValueByLang(btnMsg, lang)}
					</Link>
				</Content>
			</Container>
		</Wrapper>
	);
};
